// src/components/SidePanel.js (Updated)
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';

const SidePanel = ({ isOpen, onClose, title, children, initialWidth = 400 }) => {
    const [width, setWidth] = useState(initialWidth);
    const [isResizing, setIsResizing] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [position, setPosition] = useState(() => {
        return localStorage.getItem('side_panel_position') || 'right';
    });
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    useEffect(() => {
        if (isOpen) {
            document.documentElement.style.setProperty('--side-panel-width', `${width}px`);
            document.documentElement.style.setProperty('--side-panel-position', position);
        }
    }, [isOpen, width, position]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;
            
            let newWidth;
            if (position === 'right') {
                newWidth = startWidthRef.current + (startXRef.current - e.clientX);
            } else {
                newWidth = startWidthRef.current + (e.clientX - startXRef.current);
            }
            
            newWidth = Math.min(Math.max(newWidth, 300), window.innerWidth - 300);
            setWidth(newWidth);
            document.documentElement.style.setProperty('--side-panel-width', `${newWidth}px`);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            document.body.style.pointerEvents = '';
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'ew-resize';
            document.body.style.pointerEvents = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            document.body.style.pointerEvents = '';
        };
    }, [isResizing, position]);

    const startResize = (e) => {
        e.preventDefault();
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = width;
    };

    const togglePosition = () => {
        const newPosition = position === 'right' ? 'left' : 'right';
        setPosition(newPosition);
        localStorage.setItem('side_panel_position', newPosition);
        document.documentElement.style.setProperty('--side-panel-position', newPosition);
        window.dispatchEvent(new StorageEvent('storage', {
            key: 'side_panel_position',
            newValue: newPosition
        }));
    };

    const toggleMaximize = () => {
        if (!isMaximized) {
            const newWidth = window.innerWidth - 300;
            setWidth(newWidth);
            document.documentElement.style.setProperty('--side-panel-width', `${newWidth}px`);
            setIsMaximized(true);
        } else {
            setWidth(initialWidth);
            document.documentElement.style.setProperty('--side-panel-width', `${initialWidth}px`);
            setIsMaximized(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div 
                className={`side-panel-resize-handle ${position}`}
                onMouseDown={startResize}
                style={{
                    position: 'fixed',
                    top: 0,
                    bottom: 0,
                    [position === 'right' ? 'left' : 'right']: position === 'right' ? `calc(100% - ${width}px - 5px)` : `${width}px`,
                    width: '5px',
                    cursor: 'ew-resize',
                    backgroundColor: isResizing ? 'var(--primary-color, #6366f1)' : 'transparent',
                    transition: 'background-color 0.2s',
                    zIndex: 1001,
                    pointerEvents: 'auto'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--primary-color, #6366f1)'}
                onMouseLeave={(e) => {
                    if (!isResizing) {
                        e.target.style.backgroundColor = 'transparent';
                    }
                }}
            />
            
            <div className={`side-panel ${position} ${isMaximized ? 'maximized' : ''}`}>
                <div className="side-panel-header">
                    <h3>{title}</h3>
                    <div className="side-panel-actions">
                        <button
                            className="panel-action-btn"
                            onClick={togglePosition}
                            title={position === 'right' ? 'Move to Left' : 'Move to Right'}
                        >
                            {position === 'right' ? '←' : '→'}
                        </button>
                        <button
                            className="panel-action-btn"
                            onClick={toggleMaximize}
                            title={isMaximized ? 'Restore' : 'Maximize'}
                        >
                            <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} />
                        </button>
                        <button
                            className="panel-action-btn close-btn"
                            onClick={onClose}
                            title="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>
                <div className="side-panel-content">
                    {children}
                </div>
            </div>
        </>
    );
};

export default SidePanel;